import React, { useState, useEffect } from 'react';
import { 
  Box, 
  ImageList, 
  ImageListItem, 
  Typography, 
  CircularProgress,
  Dialog,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Paper,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
// Add Capacitor imports
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';

function Photos() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [videoUrls, setVideoUrls] = useState([]);
  const [videoLoading, setVideoLoading] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Simplified photo URL handling
  const getPhotoUrl = (photoName) => `/photos/${photoName}`;
  const getVideoUrl = (videoName) => `/photos/${videoName}`;

  useEffect(() => {
    const checkImageExists = async (url) => {
      try {
        if (Capacitor.isNativePlatform()) {
          const fullUrl = window.location.origin + url;
          const response = await fetch(fullUrl, { method: 'HEAD' });
          return response.ok;
        } else {
          const response = await fetch(url, { method: 'HEAD' });
          return response.ok;
        }
      } catch (error) {
        return false;
      }
    };

    const checkVideoExists = async (url) => {
      try {
        console.log(`檢查影片 ${url}`);
        
        if (Capacitor.isNativePlatform()) {
          const fullUrl = window.location.origin + url;
          const response = await fetch(fullUrl, { method: 'HEAD' });
          return response.ok;
        } else {
          const response = await fetch(url, { method: 'HEAD' });
          return response.ok;
        }
      } catch (error) {
        // Suppress expected fetch errors to keep console clean
        return false;
      }
    };



    const findAvailableContent = async () => {
      setLoading(true);
      const foundPhotos = [];

      // Check for videos with enhanced loading
      const videoNames = ['video.mp4', 'video2.mp4'];
      const foundVideos = [];
      
      console.log('開始檢查影片...');
      
      // We process videos in parallel
      const videoPromises = videoNames.map(async (videoName) => {
        const videoUrl = getVideoUrl(videoName);
        
        try {
          const exists = await checkVideoExists(videoUrl);
          
          if (exists) {
            console.log(`影片 ${videoName} 已加入清單`);
            return videoUrl;
          } else {
            console.log(`影片 ${videoName} 不存在或無法載入`);
            return null;
          }
        } catch (error) {
          return null;
        }
      });
      
      const videoResults = await Promise.all(videoPromises);
      foundVideos.push(...videoResults.filter(Boolean));
      
      console.log(`找到 ${foundVideos.length} 個可用影片`);
      setVideoUrls(foundVideos);

      // Check for photos - only check existing photos (photo1.jpg)
      const existingPhotos = ['photo1.jpg'];
      
      // We check photos in parallel to save time
      const photoPromises = existingPhotos.map(async (photoName) => {
        const photoUrl = getPhotoUrl(photoName);
        try {
          const exists = await checkImageExists(photoUrl);
          
          if (exists) {
            return photoName;
          }
        } catch (error) {
          // Suppress error
        }
        return null;
      });
      
      const photoResults = await Promise.all(photoPromises);
      foundPhotos.push(...photoResults.filter(Boolean));

      // 反轉照片陣列，讓較大編號的照片先顯示
      setPhotos(foundPhotos.reverse());
      setLoading(false);
      
      if (foundPhotos.length === 0 && foundVideos.length === 0) {
        setError('No photos or videos found. Make sure media files are in the correct location: public/photos/');
      }
    };

    findAvailableContent();
  }, []);

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
  };

  const handleClose = () => {
    setSelectedPhoto(null);
  };

  const handleDownload = async (photoUrl) => {
    try {
      const response = await fetch(photoUrl);
      const blob = await response.blob();

      if (Capacitor.isNativePlatform()) {
        // Android download
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          const base64Data = reader.result.split(',')[1];
          const fileName = photoUrl.split('/').pop();
          
          try {
            await Filesystem.writeFile({
              path: fileName,
              data: base64Data,
              directory: Directory.Documents,
              recursive: true
            });
            setSnackbar({
              open: true,
              message: 'Photo saved',
              severity: 'success'
            });
          } catch (error) {
            console.error('Error saving file:', error);
            setSnackbar({
              open: true,
              message: 'Failed to save photo',
              severity: 'error'
            });
          }
        };
      } else {
        // Web download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = photoUrl.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading photo:', error);
      setSnackbar({
        open: true,
        message: 'Failed to download photo',
        severity: 'error'
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  }

  if (photos.length === 0 && videoUrls.length === 0) {
    return (
      <Typography align="center" sx={{ p: 3 }}>
        Please add photos to the public/photos/ directory named as photo1.jpg, photo2.jpg, etc.
      </Typography>
    );
  }

  const handleYouTubePlay = (url) => {
    window.open(url, '_blank');
  };

  return (
    <Box sx={{ width: '100%', height: '100%', overflowY: 'auto' }}>
      {/* YouTube Videos Section */}
      <Paper elevation={0} sx={{ margin: 1, padding: 2, backgroundColor: '#FFF5F5', borderRadius: '16px', border: '1px solid #FFE4E6' }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', fontWeight: 700, color: '#BE123C' }}>
          精選影片
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<PlayArrowIcon />}
              onClick={() => handleYouTubePlay('https://youtube.com/shorts/9o4JK5N0zpA?si=EzJ72R-kPbuk1PMd')}
              sx={{ 
                backgroundColor: '#E11D48',
                color: 'white',
                borderRadius: '12px',
                textTransform: 'none',
                boxShadow: '0 4px 6px -1px rgba(225, 29, 72, 0.2), 0 2px 4px -1px rgba(225, 29, 72, 0.1)',
                '&:hover': { backgroundColor: '#BE123C', boxShadow: '0 10px 15px -3px rgba(225, 29, 72, 0.3), 0 4px 6px -2px rgba(225, 29, 72, 0.1)' },
                py: 1.5,
                transition: 'all 0.2s ease-in-out',
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
            >
              Spy trick
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<PlayArrowIcon />}
              onClick={() => handleYouTubePlay('https://youtu.be/KUibY1aU6eg?si=01OOVdveqvVv-DYv')}
              sx={{ 
                backgroundColor: '#E11D48',
                color: 'white',
                borderRadius: '12px',
                textTransform: 'none',
                boxShadow: '0 4px 6px -1px rgba(225, 29, 72, 0.2), 0 2px 4px -1px rgba(225, 29, 72, 0.1)',
                '&:hover': { backgroundColor: '#BE123C', boxShadow: '0 10px 15px -3px rgba(225, 29, 72, 0.3), 0 4px 6px -2px rgba(225, 29, 72, 0.1)' },
                py: 1.5,
                transition: 'all 0.2s ease-in-out',
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
            >
              夫妻相處
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      <Box sx={{ 
        width: '100%',
        padding: 1
      }}>
        {videoUrls.map((videoUrl, index) => (
          <Box key={index} sx={{ mb: 2, borderRadius: '8px', overflow: 'hidden', boxShadow: '0 4px 8px rgba(0,0,0,0.1)', position: 'relative' }}>
            {videoLoading[index] && (
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)', 
                  zIndex: 1,
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  borderRadius: '50%',
                  p: 1
                }}
              >
                <CircularProgress size={24} sx={{ color: 'white' }} />
              </Box>
            )}
            <video
              controls
              preload="metadata"
              muted
              playsInline
              style={{ width: '100%', display: 'block' }}
              onLoadStart={() => {
                console.log(`影片 ${index + 1} 開始載入`);
                setVideoLoading(prev => ({ ...prev, [index]: true }));
              }}
              onLoadedMetadata={() => {
                console.log(`影片 ${index + 1} 元數據載入完成`);
                setVideoLoading(prev => ({ ...prev, [index]: false }));
              }}
              onCanPlay={() => {
                console.log(`影片 ${index + 1} 可以播放`);
                setVideoLoading(prev => ({ ...prev, [index]: false }));
              }}
              onError={(e) => {
                console.error(`影片 ${index + 1} 載入錯誤:`, e);
                setVideoLoading(prev => ({ ...prev, [index]: false }));
                
                // Try to reload the video source safely
                const video = e.target;
                if (video && !video.hasAttribute('data-retry')) {
                  video.setAttribute('data-retry', 'true');
                  console.log(`將在 3 秒後重試載入影片 ${index + 1}`);
                  setTimeout(() => {
                    if (video && typeof video.load === 'function') {
                      setVideoLoading(prev => ({ ...prev, [index]: true }));
                      video.load();
                    }
                  }, 3000);
                }
              }}
              onStalled={() => {
                console.warn(`影片 ${index + 1} 載入停滯`);
                setVideoLoading(prev => ({ ...prev, [index]: true }));
              }}
              onWaiting={() => {
                console.log(`影片 ${index + 1} 等待數據`);
                setVideoLoading(prev => ({ ...prev, [index]: true }));
              }}
              onPlaying={() => {
                setVideoLoading(prev => ({ ...prev, [index]: false }));
              }}
            >
              <source 
                src={Capacitor.isNativePlatform() ? window.location.origin + videoUrl : videoUrl} 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </Box>
        ))}
        <ImageList variant="masonry" cols={1} gap={8}>
          {photos.map((photo, index) => (
            <ImageListItem 
              key={index}
              sx={{ cursor: 'pointer'}}
              onClick={() => handlePhotoClick(photo)}
            >
              <img
                src={getPhotoUrl(photo)}
                alt={`Photo ${index + 1}`}
                loading="lazy"
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}
                onError={(e) => {
                  console.error('Error loading image:', photo); // Debug log
                  e.target.style.display = 'none';
                }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      </Box>

      <Dialog
        open={Boolean(selectedPhoto)}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <Button
            variant="outlined"
            sx={{
              position: 'absolute',
              left: 4,
              top: 4, 
              color: 'gray',
              opacity: 0.5
            }}
            onClick={() => handleDownload(getPhotoUrl(selectedPhoto))}
          ><DownloadIcon />
          </Button>
          {selectedPhoto && (
            <img
              src={getPhotoUrl(selectedPhoto)}
              alt="Selected photo"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '90vh',
                objectFit: 'contain'
              }}
            />
          )}
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Photos;
